import {
  normalizePsgcCode,
  type DashboardBarangayStatistics,
} from "@rbim/shared";
import { MapPinned, RotateCcw, ZoomIn, ZoomOut } from "lucide-react";
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useMunicipalityBasemap } from "./use-municipality-basemap";
import { geographicMapBounds, municipalityFitOptions } from "./municipality-map-basemap";
import { TERRAIN_CREDITS_URL } from "./municipality-map-terrain";
import { municipalityMaskRings } from "./municipality-terrain-mask";
import { loadBoundaryFeatures } from "./philippines-population-map";
import { createPopulationScale, formatMapCount, populationColors } from "./municipality-map-population";
import {
  DEFAULT_VIEWPORT,
  boundsOf,
  createFittedProjection,
  estimateLabelSize,
  labelFontSize,
  layoutFocusedMapLabels,
  layoutMapLabels,
  zoomTransform,
  type MapBounds,
  type MapGeometry,
  type Position,
  type ViewportSize,
} from "./municipality-map-layout";

type Feature = {
  type: "Feature";
  geometry: MapGeometry;
  properties: Record<string, string | undefined>;
};

type Props = {
  locationLabel: string;
  barangays?: DashboardBarangayStatistics[];
  loading?: boolean;
  onBarangaySelect?: (barangay: DashboardBarangayStatistics | null) => void;
};

type DrawingBase = {
  feature: Feature;
  code: string;
  path: string;
  anchorPoint: Position;
  labelLines: string[];
  fontSize: number;
  bounds: MapBounds;
  labelWidth: number;
  labelHeight: number;
};
type Drawing = DrawingBase & {
  showLabel: boolean;
  labelPoint: Position;
  showLeader: boolean;
  leaderEnd: Position;
};
type BarangayIndexes = ReturnType<typeof indexBarangays>;

const minimumZoom = 1;
const maximumZoom = 1.8;
const zoomStep = 0.15;
const emptyBarangays: DashboardBarangayStatistics[] = [];

export function MunicipalityBarangayMap({
  locationLabel,
  barangays = emptyBarangays,
  loading = false,
  onBarangaySelect,
}: Props) {
  const reliefId = `municipality-relief-${useId().replace(/:/g, "")}`;
  const [features, setFeatures] = useState<Feature[]>([]);
  const [mapLoading, setMapLoading] = useState(true);
  const [mapError, setMapError] = useState("");
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [hoveredCode, setHoveredCode] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<ViewportSize>(DEFAULT_VIEWPORT);
  const basemap = useMunicipalityBasemap(features, viewport);
  const pointerStart = useRef<Position | null>(null);

  // Keep the latest parent callback without making boundary loading depend on
  // its function identity. Municipality dashboard refreshes can recreate an
  // inline callback; depending on it here used to clear/reload the map and
  // produced the visible flicker/glitch.
  const onBarangaySelectRef = useRef(onBarangaySelect);
  useEffect(() => {
    onBarangaySelectRef.current = onBarangaySelect;
  }, [onBarangaySelect]);

  useEffect(() => {
    const element = viewportRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;

    const updateViewport = (width: number, height: number) => {
      const nextWidth = Math.max(1, Math.round(width));
      const nextHeight = Math.max(360, Math.round(height));
      setViewport((current) =>
        current.width === nextWidth && current.height === nextHeight
          ? current
          : { width: nextWidth, height: nextHeight },
      );
    };
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      updateViewport(entry.contentRect.width, entry.contentRect.height);
    });
    const rect = element.getBoundingClientRect();
    updateViewport(rect.width, rect.height);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const barangayIndexes = useMemo(() => indexBarangays(barangays), [barangays]);
  const mapFeatures = features;
  const populationScale = useMemo(
    () => createPopulationScale(barangays.map((barangay) => barangay.population)),
    [barangays],
  );

  useEffect(() => {
    const controller = new AbortController();
    setMapLoading(true);
    setMapError("");
    setFeatures([]);
    setSelectedCode(null);
    setHoveredCode(null);
    setZoom(1);
    onBarangaySelectRef.current?.(null);

    void loadBoundaryFeatures("MUNICIPALITY_CITY", locationLabel, controller.signal)
      .then((loadedFeatures) => {
        if (!controller.signal.aborted) {
          setFeatures(loadedFeatures as unknown as Feature[]);
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setMapError(
            error instanceof Error
              ? error.message
              : "Map boundaries could not be loaded.",
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setMapLoading(false);
      });

    return () => controller.abort();
  }, [locationLabel]);

  const projection = useMemo(
    () => createFittedProjection(mapFeatures, viewport),
    [mapFeatures, viewport],
  );
  // Never apply the SVG-only fit to a visible basemap: use its exact camera projection.
  const project = basemap.project ?? projection?.project ?? null;
  // Reuse the terrain mask's exact dissolved boundary, with no buffers or
  // per-barangay edges that could introduce seams in the outside-only wash.
  const municipalityRings = useMemo(() => municipalityMaskRings(mapFeatures), [mapFeatures]);
  const municipalityOutline = useMemo(() => project
    ? pathOf({ type: "Polygon", coordinates: municipalityRings }, project)
    : "", [municipalityRings, project]);
  const drawings = useMemo<Drawing[]>(() => {
    if (!project) return [];

    const baseDrawings = mapFeatures.map<DrawingBase>((feature) => {
      const ring = primaryProjectedRing(feature, project);
      const bounds = boundsOf(ring);
      const statistic = statisticsOf(feature, barangayIndexes);
      const labelLines = wrapLabel(statistic?.barangayName ?? rawNameOf(feature));
      const fontSize = labelFontSize(bounds, viewport);
      const labelSize = estimateLabelSize(labelLines, fontSize);

      return {
        feature,
        code: codeOf(feature),
        path: pathOf(feature.geometry, project),
        anchorPoint: interiorPointOf(ring, viewport),
        labelLines,
        fontSize,
        bounds,
        labelWidth: labelSize.width,
        labelHeight: labelSize.height,
      };
    });

    const labelItems = baseDrawings.map((drawing) => ({
      id: drawing.code,
      anchor: drawing.anchorPoint,
      polygonBounds: drawing.bounds,
      labelWidth: drawing.labelWidth,
      labelHeight: drawing.labelHeight,
    }));
    const placements = new Map(
      (selectedCode
        ? layoutFocusedMapLabels(labelItems, viewport, selectedCode)
        : layoutMapLabels(labelItems, viewport)
      ).map((placement) => [placement.id, placement]),
    );

    return baseDrawings.map((drawing) => {
      const placement = placements.get(drawing.code);
      return {
        ...drawing,
        showLabel: Boolean(placement),
        labelPoint: placement?.point ?? drawing.anchorPoint,
        showLeader: placement?.showLeader ?? false,
        leaderEnd: placement?.leaderEnd ?? drawing.anchorPoint,
      };
    });
  }, [barangayIndexes, mapFeatures, project, selectedCode, viewport]);

  const selectedDrawing = drawings.find((drawing) => drawing.code === selectedCode) ?? null;
  const focusPoint = [viewport.width / 2, viewport.height / 2] as Position;
  const mapTransform = basemap.map ? undefined : zoomTransform(zoom, focusPoint, viewport);
  const renderOverlay = (overlay: ReactNode) => basemap.overlayContainer
    ? createPortal(overlay, basemap.overlayContainer)
    : overlay;

  const selectFeature = (drawing: Drawing) => {
    setSelectedCode(drawing.code);
    onBarangaySelectRef.current?.(statisticsOf(drawing.feature, barangayIndexes) ?? null);
    // Navigation belongs to explicit pointer/keyboard activation only, never to
    // selected-state/data effects: polling and reloads retain the settled view.
    const selectedBounds = geographicMapBounds([drawing.feature]);
    if (basemap.map && selectedBounds) basemap.map.fitBounds(selectedBounds, {
      ...municipalityFitOptions(viewport),
      maxZoom: 12.9,
      duration: 600,
      linear: true,
    });
  };

  const resetMap = () => {
    setSelectedCode(null);
    setHoveredCode(null);
    setZoom(1);
    basemap.fit();
    onBarangaySelectRef.current?.(null);
  };

  const changeZoom = (delta: number) => {
    if (basemap.map) {
      basemap.map.zoomTo(basemap.map.getZoom() + Math.sign(delta) * 0.5, { duration: 0 });
      return;
    }
    setZoom((current) =>
      Math.min(maximumZoom, Math.max(minimumZoom, current + delta)),
    );
  };

  const mappedCount = mapFeatures.length;
  const hasPopulationRecords = barangays.some((barangay) => barangay.population > 0);
  const hasPopulationStatistics = barangays.some((barangay) =>
    Number.isFinite(barangay.population) && barangay.population >= 0,
  );
  const hasMissingStatistics = drawings.some((drawing) =>
    populationScale(statisticsOf(drawing.feature, barangayIndexes)?.population).missing,
  );
  const selectedStatistic = selectedDrawing
    ? statisticsOf(selectedDrawing.feature, barangayIndexes)
    : null;

  return (
    <section className="municipality-map-panel" aria-label="Municipality/City Map">
      <header className="municipality-map-header">
        <div className="municipality-map-heading">
          <span className="municipality-map-heading-icon" aria-hidden="true">
            <MapPinned size={27} />
          </span>
          <div>
            <h2>Barangay Map</h2>
            <p>Official PSA barangay boundaries with synchronized RBIM population data.</p>
          </div>
        </div>
        <div className="municipality-map-header-meta">
          <strong>
            <MapPinned size={17} />
            {mappedCount ? `${mappedCount} Barangays` : "Barangays"}
          </strong>
          <span>PSA Barangay 2020 &bull; Click a barangay to select</span>
        </div>
      </header>

      <div className="municipality-map-stage">
        <div className="municipality-map-viewport" ref={viewportRef}>
          <div className="municipality-basemap" ref={basemap.containerRef} data-basemap-status={basemap.status} data-terrain-status={basemap.terrainStatus} />
          {mapLoading ? (
            <div className="municipality-map-message">Loading barangay boundaries&hellip;</div>
          ) : null}
          {mapError ? (
            <div className="municipality-map-message">
              <strong>Map boundary data is not yet available.</strong>
              <span>{mapError}</span>
            </div>
          ) : null}

          {!mapLoading && !mapError && project ? renderOverlay(
            <svg
              className="municipality-map-overlay"
              data-projection={basemap.map ? "EPSG:3857" : "boundary-only"}
              data-map-zoom={basemap.zoom ?? zoom}
              viewBox={`0 0 ${viewport.width} ${viewport.height}`}
              preserveAspectRatio="xMidYMid meet"
              role="group"
              aria-label={`Barangay boundaries of ${locationLabel}`}
            >
              <desc>Official WGS84 barangay boundaries projected with the geographic basemap. Population colors use synchronized RBIM counts.</desc>
              <defs>
                <mask id={`${reliefId}-outside`} maskUnits="userSpaceOnUse" maskContentUnits="userSpaceOnUse"
                  x={0} y={0} width={viewport.width} height={viewport.height} style={{ maskType: "luminance" }}>
                  <rect width={viewport.width} height={viewport.height} fill="#ffffff" />
                  <path d={municipalityOutline} transform={mapTransform} fill="#000000" fillRule="nonzero" />
                </mask>
                {/* Outline only, with a transparent interior so the basemap stays visible. */}
                <filter id={`${reliefId}-edge`} x="-10%" y="-10%" width="120%" height="120%">
                  <feMorphology in="SourceAlpha" operator="dilate" radius={1.2} result="expanded" />
                  <feComposite in="expanded" in2="SourceAlpha" operator="out" result="edge" />
                  <feFlood floodColor="#365e4b" />
                  <feComposite in2="edge" operator="in" />
                </filter>
                <filter id={`${reliefId}-selected`} x="-20%" y="-20%" width="140%" height="140%">
                  <feDropShadow dx={0} dy={0} stdDeviation={1.6} floodColor="#00bfa5" floodOpacity={0.65} />
                </filter>
              </defs>
              {/* Soft context above basemap labels, below the unchanged RBIM
                  overlay. Transparent throughout Baroy; never intercept input. */}
              <rect className="municipality-map-context-wash" width={viewport.width} height={viewport.height}
                fill="#f7f8f6" fillOpacity={0.65} mask={`url(#${reliefId}-outside)`}
                pointerEvents="none" aria-hidden="true" />
                            <path
                className="municipality-map-interior-wash"
                d={municipalityOutline}
                transform={mapTransform}
                fill="#f7f8f6"
                fillOpacity={0.42}
                fillRule="nonzero"
                pointerEvents="none"
                aria-hidden="true"
              />
              <g className="municipality-map-drawing" transform={mapTransform}>
                <g filter={`url(#${reliefId}-edge)`} pointerEvents="none" aria-hidden="true">
                  {drawings.map((drawing) => <path key={drawing.code} d={drawing.path} fill="#365e4b" fillRule="evenodd" />)}
                </g>
                {drawings.map((drawing) => {
                  const active = drawing.code === selectedCode;
                  const statistic = statisticsOf(drawing.feature, barangayIndexes);

                  return (
                    <g
                      key={drawing.code}
                      className={`map-barangay${active ? " is-selected" : ""}`}
                      role="button"
                      tabIndex={0}
                      aria-pressed={active}
                      data-barangay-code={drawing.code}
                      aria-label={barangayAriaLabel(drawing.feature, statistic)}
                      onPointerDown={(event) => { pointerStart.current = [event.clientX, event.clientY]; }}
                      onClick={(event) => {
                        const start = pointerStart.current;
                        pointerStart.current = null;
                        if (start && Math.hypot(event.clientX - start[0], event.clientY - start[1]) > 4) return;
                        selectFeature(drawing);
                      }}
                      onMouseEnter={() => setHoveredCode(drawing.code)}
                      onMouseLeave={() => setHoveredCode(null)}
                      onFocus={() => setHoveredCode(drawing.code)}
                      onBlur={() => setHoveredCode(null)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          selectFeature(drawing);
                        }
                      }}
                    >
                      <path
                        d={drawing.path}
                        fill={populationScale(statistic?.population).fill}
                        fillOpacity={drawing.code === hoveredCode ? 0.64 : 0.58}
                        fillRule="evenodd"
                      >
                        <title>{barangayAriaLabel(drawing.feature, statistic)}</title>
                      </path>
                    </g>
                  );
                })}
                <g pointerEvents="none" aria-hidden="true" fill="none">
                  {drawings.map((drawing) => <path key={drawing.code} d={drawing.path} stroke="#fff8e7" strokeWidth={0.9} vectorEffect="non-scaling-stroke" />)}
                  {drawings.filter((drawing) => drawing.code === selectedCode || drawing.code === hoveredCode).map((drawing) => (
                    <path key={drawing.code} d={drawing.path} stroke={drawing.code === selectedCode ? "#00bfa5" : "#25836c"}
                      strokeWidth={drawing.code === selectedCode ? 2.6 : 1.8} vectorEffect="non-scaling-stroke"
                      filter={drawing.code === selectedCode ? `url(#${reliefId}-selected)` : undefined} />
                  ))}
                </g>
                <g className="municipality-map-labels" pointerEvents="none" aria-hidden="true">
                  {drawings.filter((drawing) => drawing.showLabel).map((drawing) => {
                    const active = drawing.code === selectedCode;
                    const statistic = statisticsOf(drawing.feature, barangayIndexes);
                    const dark = !drawing.showLeader && populationScale(statistic?.population).dark;
                    const lineHeight = drawing.fontSize * 1.12;
                    const startY = drawing.labelPoint[1] - ((drawing.labelLines.length - 1) * lineHeight) / 2;
                    return <g key={drawing.code}>
                      {drawing.showLeader ? (
                        <line
                          className="municipality-map-leader"
                          x1={drawing.anchorPoint[0]}
                          y1={drawing.anchorPoint[1]}
                          x2={drawing.leaderEnd[0]}
                          y2={drawing.leaderEnd[1]}
                          stroke={active ? "#0b5138" : "#678d7d"}
                          vectorEffect="non-scaling-stroke"
                          pointerEvents="none"
                        />
                      ) : null}

                      <text
                        x={drawing.labelPoint[0]}
                        y={startY}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize={drawing.fontSize}
                        fontWeight={active ? 800 : 700}
                        fill={dark ? "#ffffff" : "#172f27"}
                        stroke={dark ? "#153e2e" : "#ffffff"}
                        strokeWidth={dark ? 2 : 3}
                        strokeLinejoin="round"
                        paintOrder="stroke fill"
                        pointerEvents="none"
                      >
                        {drawing.labelLines.map((line, index) => (
                          <tspan
                            key={`${drawing.code}-${index}`}
                            x={drawing.labelPoint[0]}
                            dy={index === 0 ? 0 : lineHeight}
                          >
                            {line}
                          </tspan>
                        ))}
                      </text>
                    </g>;
                  })}
                </g>
              </g>
            </svg>
          ) : null}
          {basemap.terrainStatus === "unavailable" && basemap.status !== "unavailable" ? (
            <span className="municipality-map-status" role="status">Terrain temporarily unavailable.</span>
          ) : null}
          {basemap.status === "unavailable" || (basemap.status === "loading" && mappedCount > 0) ? (
            <span className="municipality-map-status" role="status">
              {basemap.status === "unavailable" ? "Basemap temporarily unavailable." : "Loading basemap…"}
            </span>
          ) : null}
          <div className="municipality-map-attribution" aria-label="Map attribution">
            <details>
              <summary aria-label="Map credits" title="Map credits">ⓘ</summary>
              <div className="municipality-map-attribution-details">
                <a href="https://openfreemap.org/" target="_blank" rel="noreferrer">OpenFreeMap</a>
                <a href="https://openmaptiles.org/" target="_blank" rel="noreferrer">© OpenMapTiles</a>
                <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">© OpenStreetMap contributors</a>
                <span>Elevation: <a href={TERRAIN_CREDITS_URL} target="_blank" rel="noreferrer">Mapzen Terrain Tiles</a></span>
                <span>SRTM/GMTED2010 data courtesy of the U.S. Geological Survey; ETOPO1: NOAA</span>
              </div>
            </details>
          </div>
        </div>

        <div className="municipality-map-footer">
          <div className="municipality-map-legend" aria-label="Population map legend">
            <strong>Population</strong>
            <span className="municipality-map-gradient" style={{ background: `linear-gradient(90deg, ${populationColors.low}, ${populationColors.middle}, ${populationColors.high})` }} />
            <div>
              <span>Lower</span>
              <span>Higher</span>
            </div>
            {hasMissingStatistics ? <small>Gray: statistics unavailable</small> : null}
          </div>

          <div className="municipality-map-selection" aria-live="polite">
            {selectedStatistic ? (
              <>
                <span>Selected Barangay</span>
                <strong>{selectedStatistic.barangayName}</strong>
                <small>
                  Population: {formatMapCount(selectedStatistic.population)} &bull;{" "}
                  Households: {formatMapCount(selectedStatistic.households)} &bull;{" "}
                  Families: {formatMapCount(selectedStatistic.families)}
                </small>
              </>
            ) : (
              <>
                <span>{selectedDrawing ? "Selected Barangay" : "Map view"}</span>
                <strong>{selectedDrawing ? rawNameOf(selectedDrawing.feature) : locationLabel}</strong>
                <small>{selectedDrawing ? "Synchronized statistics unavailable." : "Select a barangay to update dashboard statistics."}</small>
              </>
            )}
          </div>

          <div className="municipality-map-toolbar" aria-label="Map controls">
            <button
              type="button"
              aria-label="Zoom in"
              title="Zoom in"
              disabled={basemap.zoom !== null ? basemap.zoom >= 18 : zoom >= maximumZoom}
              onClick={() => changeZoom(zoomStep)}
            >
              <ZoomIn size={20} />
            </button>
            <button
              type="button"
              aria-label="Zoom out"
              title="Zoom out"
              disabled={basemap.zoom !== null ? basemap.zoom <= 2 : zoom <= minimumZoom}
              onClick={() => changeZoom(-zoomStep)}
            >
              <ZoomOut size={20} />
            </button>
            <button
              type="button"
              aria-label="Reset map"
              title="Reset map"
              onClick={resetMap}
            >
              <RotateCcw size={19} />
            </button>
          </div>
        </div>
      </div>

      {!loading && !hasPopulationRecords ? (
        <p className="municipality-map-no-records">
          {hasPopulationStatistics ? "No synchronized population records yet." : "Synchronized statistics unavailable."}
        </p>
      ) : null}
    </section>
  );
}

function rawNameOf(feature: Feature) {
  return feature.properties.brgy_name ?? feature.properties.city_name ?? "Unnamed barangay";
}

function codeOf(feature: Feature) {
  return normalizePsgcCode(
    feature.properties.psgc_10d ??
    feature.properties.brgy_code ??
    feature.properties.city_code,
  ) ?? rawNameOf(feature);
}

function normalizeGeographyName(value: string) {
  return value
    .toLowerCase()
    .replace(/\b(barangay|brgy\.?)[\s-]*/g, "")
    .replace(/[^a-z0-9]/g, "");
}

function indexBarangays(barangays: DashboardBarangayStatistics[]) {
  const byCode = new Map<string, DashboardBarangayStatistics>();
  const byName = new Map<string, DashboardBarangayStatistics>();
  const ambiguousNames = new Set<string>();

  for (const barangay of barangays) {
    const code = normalizePsgcCode(barangay.psgcCode);
    if (code) byCode.set(code, barangay);

    const normalizedName = normalizeGeographyName(barangay.barangayName);
    if (!normalizedName || ambiguousNames.has(normalizedName)) continue;

    const existing = byName.get(normalizedName);
    if (existing && existing.barangayId !== barangay.barangayId) {
      byName.delete(normalizedName);
      ambiguousNames.add(normalizedName);
    } else {
      byName.set(normalizedName, barangay);
    }
  }

  return { byCode, byName };
}

function statisticsFromProperties(
  properties: Record<string, string | undefined>,
  indexes: BarangayIndexes,
) {
  const rawCodes = [properties.psgc_10d, properties.brgy_code]
    .filter((value): value is string => Boolean(value?.trim()));

  for (const rawCode of rawCodes) {
    const code = normalizePsgcCode(rawCode);
    if (code) {
      const statistic = indexes.byCode.get(code);
      if (statistic) return statistic;
    }
  }

  // If the boundary source supplied a numeric PSGC and it cannot be reconciled,
  // do not fall back by name. A conflicting code must never inherit another
  // Barangay's statistics merely because its display name happens to match.
  if (rawCodes.length > 0) {
    return undefined;
  }

  const rawName = properties.brgy_name;
  return rawName
    ? indexes.byName.get(normalizeGeographyName(rawName))
    : undefined;
}

function statisticsOf(feature: Feature, indexes: BarangayIndexes) {
  return statisticsFromProperties(feature.properties, indexes);
}

export function resolveBarangayMapStatistic(
  properties: Record<string, string | undefined>,
  barangays: DashboardBarangayStatistics[],
) {
  return statisticsFromProperties(properties, indexBarangays(barangays));
}

function barangayAriaLabel(
  feature: Feature,
  statistic: DashboardBarangayStatistics | undefined,
) {
  const displayName = statistic
    ? statistic.barangayName
    : rawNameOf(feature);
  if (!statistic) return `${displayName}. No synchronized RBIM population available.`;
  return `${displayName}. Population ${formatMapCount(statistic.population)}, households ${formatMapCount(statistic.households)}, families ${formatMapCount(statistic.families)}.`;
}

function pathOf(geometry: MapGeometry, project: (point: Position) => Position) {
  const polygons = geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates;
  return polygons
    .map((polygon) =>
      polygon
        .map(
          (ring) =>
            ring
              .map((point, index) => {
                const [x, y] = project(point);
                return `${index ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`;
              })
              .join(" ") + " Z",
        )
        .join(" "),
    )
    .join(" ");
}

function primaryProjectedRing(feature: Feature, project: (point: Position) => Position) {
  const rings =
    feature.geometry.type === "Polygon"
      ? [feature.geometry.coordinates[0] ?? []]
      : feature.geometry.coordinates.map((polygon) => polygon[0] ?? []);

  return rings
    .map((ring) => ring.map(project))
    .sort((a, b) => Math.abs(ringArea(b)) - Math.abs(ringArea(a)))[0] ?? [];
}

function ringArea(ring: Position[]) {
  let area = 0;
  for (let index = 0; index < ring.length; index += 1) {
    const [x1, y1] = ring[index] ?? [0, 0];
    const [x2, y2] = ring[(index + 1) % ring.length] ?? [0, 0];
    area += x1 * y2 - x2 * y1;
  }
  return area / 2;
}

function polygonCentroid(ring: Position[]): Position | null {
  let twiceArea = 0;
  let x = 0;
  let y = 0;

  for (let index = 0; index < ring.length; index += 1) {
    const [x1, y1] = ring[index] ?? [0, 0];
    const [x2, y2] = ring[(index + 1) % ring.length] ?? [0, 0];
    const cross = x1 * y2 - x2 * y1;
    twiceArea += cross;
    x += (x1 + x2) * cross;
    y += (y1 + y2) * cross;
  }

  if (Math.abs(twiceArea) < 0.0001) return null;
  return [x / (3 * twiceArea), y / (3 * twiceArea)];
}

function pointInside(point: Position, ring: Position[]) {
  const [x, y] = point;
  let inside = false;

  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i] ?? [0, 0];
    const [xj, yj] = ring[j] ?? [0, 0];
    const intersects =
      yi > y !== yj > y &&
      x < ((xj - xi) * (y - yi)) / (yj - yi || Number.EPSILON) + xi;
    if (intersects) inside = !inside;
  }

  return inside;
}

function distanceToSegment(point: Position, start: Position, end: Position) {
  const [px, py] = point;
  const [x1, y1] = start;
  const [x2, y2] = end;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return Math.hypot(px - x1, py - y1);
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lengthSquared));
  const x = x1 + t * dx;
  const y = y1 + t * dy;
  return Math.hypot(px - x, py - y);
}

function edgeDistance(point: Position, ring: Position[]) {
  let minimum = Number.POSITIVE_INFINITY;
  for (let index = 0; index < ring.length; index += 1) {
    minimum = Math.min(
      minimum,
      distanceToSegment(
        point,
        ring[index] ?? [0, 0],
        ring[(index + 1) % ring.length] ?? [0, 0],
      ),
    );
  }
  return minimum;
}

function interiorPointOf(ring: Position[], viewport: ViewportSize): Position {
  if (!ring.length) return [viewport.width / 2, viewport.height / 2];
  const bounds = boundsOf(ring);
  const candidates: Position[] = [];
  const centroid = polygonCentroid(ring);
  if (centroid) candidates.push(centroid);
  candidates.push([
    (bounds.minX + bounds.maxX) / 2,
    (bounds.minY + bounds.maxY) / 2,
  ]);

  for (let yStep = 1; yStep < 10; yStep += 1) {
    for (let xStep = 1; xStep < 10; xStep += 1) {
      candidates.push([
        bounds.minX + (bounds.width * xStep) / 10,
        bounds.minY + (bounds.height * yStep) / 10,
      ]);
    }
  }

  const inside = candidates.filter((candidate) => pointInside(candidate, ring));
  if (!inside.length) {
    return (
      ring[Math.floor(ring.length / 2)] ??
      [viewport.width / 2, viewport.height / 2]
    );
  }

  return inside.reduce((best, candidate) =>
    edgeDistance(candidate, ring) > edgeDistance(best, ring) ? candidate : best,
  );
}

function wrapLabel(value: string) {
  const words = value.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 1 || value.length <= 12) return [value];

  let best = [words[0] ?? value, words.slice(1).join(" ")];
  let bestDifference = Math.abs(best[0].length - best[1].length);

  for (let index = 1; index < words.length; index += 1) {
    const left = words.slice(0, index).join(" ");
    const right = words.slice(index).join(" ");
    const difference = Math.abs(left.length - right.length);
    if (difference < bestDifference) {
      best = [left, right];
      bestDifference = difference;
    }
  }

  return best.filter(Boolean);
}


